import { useMemo, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Loader2, Package, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { allUnitOptions, getCompatibleUnits } from '@/lib/inventory-units'
import {
  MODE_LABELS,
  SCOPE_LABELS,
  costFixPath,
  costPerStockUnit,
  defaultUnitFor,
  largestUnitFor,
  lineFigures,
  newLine,
  rateInUnit,
  round5,
  rowGuards,
  type RecipeLineDraft,
  type RecipeLineMode,
  type RecipeScope,
  type StockItemRef,
} from '@/lib/recipeEditor'
import { canAccessAdminRoute, getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import { IngredientPicker } from './IngredientPicker'

export interface RecipeEditorProps {
  /** Product recipes carry a scope per row; option recipes also carry a mode. */
  kind: 'product' | 'option'
  lines: RecipeLineDraft[]
  onChange: (lines: RecipeLineDraft[]) => void
  items: Record<string, StockItemRef>
  itemList: StockItemRef[]
  /**
   * The price a single line must not exceed. For a product, its price; for an
   * option, the highest price an item sells for with it (0 = no cap known).
   */
  price: number
  /** Servings the batch makes (product) — 1 for options. */
  yieldN: number
  formatMoney: (n: number) => string
  /** "New ingredient": what was typed, and the row it should land on (null = a new row). */
  onCreateIngredient?: (query: string, rowKey: string | null) => void
  /** Set one ingredient's cost, per its STOCK unit. Resolves true when saved. */
  onSaveCost?: (item: StockItemRef, costPerStockUnit: number) => Promise<boolean>
}

const SCOPES: RecipeScope[] = ['all', 'takeaway', 'dine_in']
const MODES: RecipeLineMode[] = ['add', 'replace']

/** A labelled cell; the label only shows below `sm`, where the column header is hidden (U-21). */
function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:hidden">{label}</span>
      {children}
    </div>
  )
}

export function RecipeEditor(props: RecipeEditorProps) {
  const { kind, lines, onChange, items, itemList, price, yieldN, formatMoney, onCreateIngredient, onSaveCost } = props
  const [freshKey, setFreshKey] = useState<string | null>(null)

  const update = (key: string, patch: Partial<RecipeLineDraft>) =>
    onChange(
      lines.map((l) => {
        if (l.key !== key) return l
        const next = { ...l, ...patch }
        // A new ingredient resets the unit to the smallest of its family, so
        // "130" is never silently read as litres.
        if (patch.stock_item_id && patch.stock_item_id !== l.stock_item_id) {
          const it = items[patch.stock_item_id]
          next.unit = it ? defaultUnitFor(it.unit) : ''
        }
        return next
      }),
    )
  const remove = (key: string) => onChange(lines.filter((l) => l.key !== key))
  const add = (over: Partial<RecipeLineDraft> = {}) => {
    const l = newLine(undefined, over)
    setFreshKey(l.key)
    onChange([...lines, l])
  }

  const guards = useMemo(
    () => rowGuards(lines, items, price, yieldN, formatMoney, kind),
    [lines, items, price, yieldN, formatMoney, kind],
  )

  const usedByScope = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const l of lines) {
      const k = `${l.scope}|${kind === 'option' ? l.mode : ''}`
      if (!m.has(k)) m.set(k, new Set())
      if (l.stock_item_id) m.get(k)!.add(l.stock_item_id)
    }
    return m
  }, [lines, kind])

  // Tailwind only emits classes it can SEE written out in source. These used
  // to be assembled at runtime (`cols.replace('grid-cols-[', 'sm:grid-cols-[')`),
  // so the row's responsive grid was never generated and every ingredient row
  // stacked into one column on dev. Both spellings stay literal.
  const cols = kind === 'option'
    ? 'sm:grid-cols-[minmax(0,1fr)_88px_120px_110px_170px_36px]'
    : 'sm:grid-cols-[minmax(0,1fr)_88px_120px_170px_36px]'

  return (
    <div className="space-y-2">
      {lines.length > 0 && (
        <div className={cn('hidden gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:grid', cols)}>
          <span>Ingredient</span>
          <span>Qty</span>
          <span>Unit</span>
          {kind === 'option' && <span>Effect</span>}
          <span>When</span>
          <span />
        </div>
      )}
      <ul className="space-y-1.5">
        {lines.map((l) => {
          const item = items[l.stock_item_id]
          const fig = lineFigures(l, item, yieldN)
          const rowG = guards[l.key] ?? []
          const blocked = rowG.some((g) => g.level === 'block')
          const compatible = item ? getCompatibleUnits(item.unit) : []
          const used = usedByScope.get(`${l.scope}|${kind === 'option' ? l.mode : ''}`) ?? new Set<string>()
          // F-FE-N165: a stored unit outside the list must be SHOWN, not
          // masked — a <select> whose value matches no option displays the
          // first one ("Each / Piece"), and re-picking it fires no change, so
          // the row could never be corrected.
          const unitOptions = (compatible.length ? compatible : [l.unit]).filter(Boolean)
          const strayUnit = l.unit && !unitOptions.includes(l.unit) ? l.unit : null
          return (
            <li key={l.key} className={cn('rounded-lg border p-2', blocked && 'border-destructive/60 bg-destructive/5')}>
              <div className={cn('grid grid-cols-1 items-end gap-2', cols)}>
                <Cell label="Ingredient">
                  <IngredientPicker
                    value={l.stock_item_id}
                    items={itemList}
                    onChange={(id) => update(l.key, { stock_item_id: id })}
                    onCreateNew={onCreateIngredient ? (q) => onCreateIngredient(q, l.key) : undefined}
                    formatMoney={formatMoney}
                    disabledIds={used}
                    autoOpen={l.key === freshKey && !l.stock_item_id}
                  />
                </Cell>
                <Cell label="Qty">
                  <Input
                    aria-label="Quantity"
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    placeholder="0"
                    className="h-9 tabular-nums"
                    value={l.qty}
                    onChange={(e) => update(l.key, { qty: e.target.value })}
                  />
                </Cell>
                <Cell label="Unit">
                  <select
                    aria-label="Unit"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                    value={l.unit}
                    disabled={!item}
                    onChange={(e) => update(l.key, { unit: e.target.value })}
                  >
                    {strayUnit && (
                      <option value={strayUnit} disabled>
                        {strayUnit} — not a unit for this item
                      </option>
                    )}
                    {unitOptions.map((u) => (
                      <option key={u} value={u}>
                        {allUnitOptions.find((o) => o.value === u)?.label ?? u}
                      </option>
                    ))}
                  </select>
                </Cell>
                {kind === 'option' && (
                  <Cell label="Effect">
                    <select
                      aria-label="Effect on the base recipe"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={l.mode}
                      onChange={(e) => update(l.key, { mode: e.target.value as RecipeLineMode })}
                      title="Adds: on top of the base recipe. Replaces: instead of the base line for the same ingredient."
                    >
                      {MODES.map((m) => (
                        <option key={m} value={m}>{MODE_LABELS[m]}</option>
                      ))}
                    </select>
                  </Cell>
                )}
                <Cell label="When">
                  <select
                    aria-label="When this ingredient is used"
                    className={cn(
                      'flex h-9 w-full rounded-md border bg-background px-2 text-sm',
                      l.scope === 'all' ? 'border-input text-muted-foreground' : 'border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300',
                    )}
                    value={l.scope}
                    onChange={(e) => update(l.key, { scope: e.target.value as RecipeScope })}
                  >
                    {SCOPES.map((s) => (
                      <option key={s} value={s}>{SCOPE_LABELS[s]}</option>
                    ))}
                  </select>
                </Cell>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 justify-self-end text-muted-foreground hover:text-destructive" onClick={() => remove(l.key)} aria-label="Remove ingredient">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-1 text-xs">
                <span className={cn('tabular-nums', blocked ? 'text-destructive' : 'text-muted-foreground')}>
                  {!item ? (
                    l.stock_item_id ? 'This ingredient is not in the list any more.' : 'Pick an ingredient to see its cost.'
                  ) : !fig ? (
                    l.qty.trim() === '' ? 'Type the quantity.' : 'That unit does not convert — pick one from the list.'
                  ) : fig.costed ? (
                    <>
                      {round5(fig.perServingQty)} {item.unit} per serving × {formatMoney(item.unitCost ?? 0)} ={' '}
                      <span className="font-medium text-foreground">{formatMoney(fig.cost)}</span>
                      {yieldN > 1 && <span className="text-muted-foreground"> · batch {l.qty} {l.unit} ÷ {yieldN}</span>}
                    </>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-300">
                      No cost on {item.name} yet — it deducts stock but adds nothing to cost.
                    </span>
                  )}
                </span>
                {item && fig && !fig.costed && onSaveCost && (
                  <SetCostPopover item={item} formatMoney={formatMoney} onSave={onSaveCost} label="Set cost" />
                )}
              </div>
              {rowG.map((g, gi) => (
                <div key={`${g.kind}-${gi}`} role="alert" className="mt-1 flex flex-wrap items-center gap-2 rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                  <span className="min-w-0 flex-1">{g.message}</span>
                  {g.kind === 'cost' && item && onSaveCost && (
                    <SetCostPopover item={item} formatMoney={formatMoney} onSave={onSaveCost} label={`Fix ${item.name}'s cost`} tone="destructive" />
                  )}
                  {g.kind === 'cost' && g.swap && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 border-destructive/40 px-2 text-xs"
                      onClick={() => update(l.key, { unit: g.swap!.unit })}
                    >
                      Change to {g.swap.qty} {g.swap.unit}
                      {g.swap.cost != null ? ` (${formatMoney(g.swap.cost)})` : ''}
                    </Button>
                  )}
                </div>
              ))}
            </li>
          )
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => add()}>
          <Plus className="h-3.5 w-3.5" /> Add ingredient
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => add({ scope: 'takeaway' })}
          title="An ingredient that is only used when the order leaves: a cup, a lid, a bag."
        >
          <Package className="h-3.5 w-3.5" /> Add takeaway packaging
        </Button>
        {onCreateIngredient && (
          <button type="button" className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground" onClick={() => onCreateIngredient('', null)}>
            New ingredient
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Set an ingredient's cost in whatever unit the invoice uses (U-4): pick
 * "per liter" on an ml-stocked item and it is converted to per-ml. Shows the
 * current rate both ways, so "Rs 100 per ml = Rs 100,000 per liter" is on the
 * screen when the rate is the thing that is wrong.
 */
function SetCostPopover({
  item,
  formatMoney,
  onSave,
  label,
  tone,
}: {
  item: StockItemRef
  formatMoney: (n: number) => string
  onSave: (item: StockItemRef, costPerStockUnit: number) => Promise<boolean>
  label: string
  tone?: 'destructive'
}) {
  const family = getCompatibleUnits(item.unit)
  const [open, setOpen] = useState(false)
  const [v, setV] = useState('')
  const [unit, setUnit] = useState(() => largestUnitFor(item.unit))
  const [busy, setBusy] = useState(false)
  const n = Number(v)
  const typed = v.trim() !== '' && Number.isFinite(n) && n > 0
  const perStock = typed ? costPerStockUnit(n, unit, item.unit) : null
  // stock_items.default_unit_cost keeps 2 decimals: a per-unit figure under
  // half a paisa would store as 0 and read as "no cost".
  const tooSmall = perStock != null && perStock < 0.005
  const valid = perStock != null && perStock > 0 && !tooSmall
  const larger = largestUnitFor(item.unit)
  const currentLarger = item.unitCost != null && larger !== item.unit ? rateInUnit(item.unitCost, item.unit, larger) : null
  // W-1: a receipt average is not changed by a typed price, so point at the receipt.
  const receipts = costFixPath(item) === 'receipts'
  const canOpenInventory = receipts && canAccessAdminRoute(getStoredUserRole(), '/admin/inventory')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline" className={cn('h-6 px-2 text-xs', tone === 'destructive' && 'border-destructive/40')}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2">
        <p className="text-sm font-medium">{item.name}</p>
        {item.unitCost != null ? (
          <p className="text-xs text-muted-foreground">
            {receipts ? 'Recipes use the average purchase cost: ' : 'Now '}
            {formatMoney(item.unitCost)} per {item.unit}
            {currentLarger != null ? ` = ${formatMoney(currentLarger)} per ${larger}` : ''}.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">No cost yet.</p>
        )}
        {receipts ? (
          <>
            <p className="text-xs text-muted-foreground">
              That average is worked out from the stock you received, so a price typed here would not change it. If a delivery was entered at the wrong price, correct that receipt and the average is rebuilt from it.
            </p>
            {canOpenInventory ? (
              <Link
                to="/admin/inventory"
                search={{ tab: 'items', item: item.id }}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                Open {item.name} in Inventory
              </Link>
            ) : (
              <p className="text-xs text-muted-foreground">An admin or inventory manager can correct the receipt in Inventory, under History.</p>
            )}
          </>
        ) : (
          <>
          <p className="text-xs text-muted-foreground">
            Enter what you pay, in the unit on your invoice. Receiving stock later replaces this with a real average.
          </p>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} step="any" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="Price" className="h-9" autoFocus aria-label={`Price for ${item.name}`} />
            <span className="shrink-0 text-xs text-muted-foreground">per</span>
            <select
              aria-label="Unit the price is for"
              className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            >
              {family.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          {typed && perStock != null && !tooSmall && unit !== item.unit && (
            <p className="text-xs text-muted-foreground">= {formatMoney(perStock)} per {item.unit}</p>
          )}
          {tooSmall && (
            <p className="text-xs text-destructive">
              That is less than half a paisa per {item.unit}, which can&apos;t be stored. Stock this item in a larger unit instead.
            </p>
          )}
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              disabled={!valid || busy}
              onClick={async () => {
                if (perStock == null) return
                setBusy(true)
                try {
                  if (await onSave(item, perStock)) {
                    setOpen(false)
                    setV('')
                  }
                } finally {
                  setBusy(false)
                }
              }}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save cost'}
            </Button>
          </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
